// Admin API for DIOS10
import apiClient from './client';
import { API_ENDPOINTS } from './config';

export const adminApi = {
    // Authentication
    login: async (email, password) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.LOGIN, { email, password });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Dashboard
    getKPIs: async (days = 30) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.DASHBOARD_KPIS, { params: { days } });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getUserGrowth: async (days = 30) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.DASHBOARD_USER_GROWTH, {
                params: { days }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getEconomicFlows: async (days = 30) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.DASHBOARD_ECONOMIC_FLOWS, {
                params: { days }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },
    getCardRarityDistribution: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.DASHBOARD_CARD_RARITY_DISTRIBUTION);
            return response;
        } catch (error) {
            throw error;
        }
    },
    getGameWeekTrends: async (days = 90) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.DASHBOARD_GAMEWEEK_TRENDS, {
                params: { days }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Users Management
    getUsers: async (filters = {}, limit = 100, offset = 0) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.USERS, {
                params: { ...filters, limit, offset }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getUser: async (id) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GET_USER(id));
            return response;
        } catch (error) {
            throw error;
        }
    },

    getSupportTickets: async (filters = {}, limit = 100, offset = 0) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.SUPPORT_TICKETS, {
                params: { ...filters, limit, offset }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateSupportTicketStatus: async (id, status) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.SUPPORT_TICKET_STATUS(id), { status });
            return response;
        } catch (error) {
            throw error;
        }
    },

    banUser: async (id, banData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.BAN_USER(id), banData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    unbanUser: async (id, banData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.UNBAN_USER(id), banData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    adjustBalance: async (id, balanceData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.ADJUST_BALANCE(id), balanceData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getPaypalDeposits: async (status) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.PAYPAL_DEPOSITS, {
                params: status === undefined ? {} : { status }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    confirmPaypalDeposit: async (id, amount) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.CONFIRM_PAYPAL_DEPOSIT(id), { amount });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getPromoCode: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.PROMO_CODE);
            return response;
        } catch (error) {
            throw error;
        }
    },

    savePromoCode: async (data) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.PROMO_CODE, data);
            return response;
        } catch (error) {
            throw error;
        }
    },

    deactivatePromoCode: async () => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.ADMIN.PROMO_CODE);
            return response;
        } catch (error) {
            throw error;
        }
    },

    grantUserBonus: async (userId, amount) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.PROMO_CODE_GRANT_BONUS, {
                userId,
                amount
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Admin Management (SUPER_ADMIN only)
    getAllAdmins: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.ADMINS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    createAdmin: async (adminData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.ADMINS, adminData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    promoteUserToAdmin: async (userId, role = 'ADMIN', password) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.PROMOTE_USER, {
                userId,
                role,
                password
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateAdminRole: async (id, role) => {
        try {
            const response = await apiClient.put(`${API_ENDPOINTS.ADMIN.ADMINS}/${id}/role`, { role });
            return response;
        } catch (error) {
            throw error;
        }
    },

    deactivateAdmin: async (id) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.DEACTIVATE_ADMIN(id));
            return response;
        } catch (error) {
            throw error;
        }
    },

    // GameWeeks Management
    getGameWeeks: async () => {
        try {
            // Get all gameweeks (not just active ones) for admin view
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GET_ALL_GAMEWEEKS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getGameWeek: async (gameweekId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GET_GAMEWEEK(gameweekId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    createGameWeek: async (gameWeekData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.CREATE_GAMEWEEK, gameWeekData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    deleteGameWeek: async (gameweekId) => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.ADMIN.DELETE_GAMEWEEK(gameweekId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    deleteAllGameWeeks: async () => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.ADMIN.DELETE_ALL_GAMEWEEKS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getGameWeekPrizeBreakdown: async (tournamentId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GAMEWEEK_PRIZE_BREAKDOWN(tournamentId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    getGameWeekTournaments: async (tournamentId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GAMEWEEK_TOURNAMENTS(tournamentId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    getTournamentDetails: async (tournamentId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.TOURNAMENT_DETAILS(tournamentId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateGameWeekStatus: async (tournamentId, status) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.UPDATE_GAMEWEEK_STATUS(tournamentId), { status });
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateGameWeekHidePrize: async (gameweekId, hidePrize) => {
        try {
            const response = await apiClient.patch(API_ENDPOINTS.ADMIN.GAMEWEEK_HIDE_PRIZE(gameweekId), { hidePrize });
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateGameWeekPrizes: async (gameweekId, { allocatedPrize, tournamentPrizes }) => {
        try {
            const response = await apiClient.patch(API_ENDPOINTS.ADMIN.GAMEWEEK_PRIZES(gameweekId), {
                allocatedPrize,
                tournamentPrizes
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    /** dryRun: solo anteprima senza scrivere sul DB */
    recalculateGameWeekPrizePools: async (gameweekId, dryRun = true) => {
        try {
            const q = dryRun ? '?dryRun=true' : '';
            const response = await apiClient.post(
                `${API_ENDPOINTS.ADMIN.GAMEWEEK_RECALC_PRIZE_POOLS(gameweekId)}${q}`,
                {}
            );
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateGameWeekSchedule: async (gameweekId, payload) => {
        try {
            const response = await apiClient.patch(
                API_ENDPOINTS.ADMIN.GAMEWEEK_PATCH_SCHEDULE(gameweekId),
                payload
            );
            return response;
        } catch (error) {
            throw error;
        }
    },

    getGameWeekSchedule: async (weeks = 4) => {
        try {
            const response = await apiClient.get(`${API_ENDPOINTS.ADMIN.GAMEWEEK_SCHEDULE}?weeks=${weeks}`);
            return response;
        } catch (error) {
            throw error;
        }
    },

    generateGameWeeks: async (numberOfWeeks = 4, config = {}) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.GAMEWEEK_GENERATE, {
                numberOfWeeks,
                ...config
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    generateMultipleGameWeeks: async (gameweeks) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.GAMEWEEK_GENERATE_MULTIPLE, {
                gameweeks
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getActiveGameWeeks: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GAMEWEEK_ACTIVE);
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateGameWeekStatuses: async () => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.GAMEWEEK_UPDATE_STATUSES);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getPrizePoolsSummary: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.PRIZE_POOLS_SUMMARY);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getRankingPeriods: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.RANKING_PERIODS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getFirstMonthlyRankingSnapshot: async (limit = 100) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.RANKING_MONTHLY_FIRST, {
                params: { limit }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getSecondaryMarketSettings: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.SECONDARY_MARKET_SETTINGS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    setSecondaryMarketSettings: async (divinityCoachOnly) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.SECONDARY_MARKET_SETTINGS, {
                divinityCoachOnly: !!divinityCoachOnly
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getMondialiNextMatchSync: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.MONDIALI_NEXT_MATCH_SYNC);
            return response;
        } catch (error) {
            throw error;
        }
    },

    setMondialiNextMatchSyncMode: async (mode) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.MONDIALI_NEXT_MATCH_SYNC, { mode });
            return response;
        } catch (error) {
            throw error;
        }
    },

    runMondialiNextMatchSync: async () => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MONDIALI_SYNC_NEXT_MATCH, {});
            return response;
        } catch (error) {
            throw error;
        }
    },

    getMondialiNextMatchSyncRunStatus: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.MONDIALI_SYNC_NEXT_MATCH_STATUS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getMondialiConvocated: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.MONDIALI_CONVOCATED, {
                timeout: 20000,
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    refreshMondialiConvocated: async () => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MONDIALI_CONVOCATED_REFRESH, {}, {
                timeout: 20000,
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getMondialiConvocatedStatus: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.MONDIALI_CONVOCATED_STATUS, {
                timeout: 20000,
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getMonthlyRankingSnapshotByPeriod: async (periodKey = 'first', limit = 100) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.RANKING_MONTHLY_BY_PERIOD(periodKey), {
                params: { limit }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    publishMonthlyRanking: async (periodKey, category, prizePool) => {
        try {
            const response = await apiClient.post(
                API_ENDPOINTS.ADMIN.RANKING_MONTHLY_PUBLISH(periodKey),
                { category, prizePool }
            );
            return response;
        } catch (error) {
            throw error;
        }
    },

    getMonthlyRankingPayoutPreview: async (periodKey, category) => {
        try {
            const response = await apiClient.get(
                API_ENDPOINTS.ADMIN.RANKING_MONTHLY_PAYOUT_PREVIEW(periodKey, category)
            );
            return response;
        } catch (error) {
            throw error;
        }
    },

    payMonthlyRanking: async (periodKey, category) => {
        try {
            const response = await apiClient.post(
                API_ENDPOINTS.ADMIN.RANKING_MONTHLY_PAY(periodKey, category),
                {}
            );
            return response;
        } catch (error) {
            throw error;
        }
    },

    closeRankingPeriod: async (type) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.RANKING_CLOSE, { type });
            return response;
        } catch (error) {
            throw error;
        }
    },

    finalizeAuctionRevenue: async () => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.FINALIZE_AUCTION_REVENUE);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getGameweekTeamCardsXp: async (gameweekId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GAMEWEEK_TEAM_CARDS_XP(gameweekId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    fixXpZeroCards: async (dryRun = false) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.XP_FIX_ZERO_CARDS, { dry_run: dryRun });
            return response;
        } catch (error) {
            throw error;
        }
    },

    fixXpZeroForGameweek: async (gameweekId, dryRun = false) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.GAMEWEEK_FIX_XP_ZERO(gameweekId), {
                dry_run: dryRun,
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getXpFixZeroCardsStatus: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.XP_FIX_ZERO_CARDS_STATUS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Scores Management
    getRawScores: async (gameweekId, playerId = null) => {
        try {
            const url = API_ENDPOINTS.ADMIN.SCORES_RAW(gameweekId);
            const response = await apiClient.get(url, {
                params: playerId ? { playerId } : {}
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getFinalScore: async (playerScoreId, cardId = null) => {
        try {
            const url = API_ENDPOINTS.ADMIN.SCORES_FINAL(playerScoreId);
            const response = await apiClient.get(url, {
                params: cardId ? { cardId } : {}
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    overrideScore: async (overrideData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.SCORES_OVERRIDE, overrideData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getScoreOverrideHistory: async (playerScoreId = null, gameweekId = null) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.SCORES_OVERRIDE_HISTORY, {
                params: {
                    ...(playerScoreId && { playerScoreId }),
                    ...(gameweekId && { gameweekId })
                }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Prizes Management
    calculatePrizeDistribution: async (tournamentId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.PRIZES_CALCULATE(tournamentId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    payPrizes: async (tournamentId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.PRIZES_PAY(tournamentId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    payPrizesForGameWeek: async (gameweekId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.GAMEWEEK_PAY_PRIZES(gameweekId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    recalcScoresForGameWeek: async (gameweekId, dry = true) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.GAMEWEEK_RECALC_SCORES(gameweekId), { dry });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getRecalcScoresStatus: async (gameweekId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GAMEWEEK_RECALC_SCORES_STATUS(gameweekId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    /** Avvia SportMonks → player_scores in background (202). Poll getSyncMatchStatsStatus. */
    syncMatchStatsForGameWeek: async (gameweekId) => {
        try {
            const response = await apiClient.post(
                API_ENDPOINTS.ADMIN.GAMEWEEK_SYNC_MATCH_STATS(gameweekId),
                {},
                { timeout: 35000 }
            );
            return response;
        } catch (error) {
            throw error;
        }
    },

    getSyncMatchStatsStatus: async (gameweekId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GAMEWEEK_SYNC_MATCH_STATS_STATUS(gameweekId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    refreshTooltipAndScoreForGameWeek: async (gameweekId) => {
        try {
            const response = await apiClient.post(
                API_ENDPOINTS.ADMIN.GAMEWEEK_REFRESH_TOOLTIP_SCORE(gameweekId),
                {},
                { timeout: 35000 }
            );
            return response;
        } catch (error) {
            throw error;
        }
    },

    getRefreshTooltipAndScoreStatus: async (gameweekId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GAMEWEEK_REFRESH_TOOLTIP_SCORE_STATUS(gameweekId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    getPrizeHistory: async (tournamentId = null, limit = 100) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.PRIZES_HISTORY, {
                params: {
                    ...(tournamentId && { tournamentId }),
                    limit
                }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },
    allocateGuaranteedPrize: async (allocationData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.PRIZES_ALLOCATE, allocationData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Treasury Management
    getTreasuryFunds: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.TREASURY_FUNDS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    transferFunds: async (transferData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.TREASURY_TRANSFER, transferData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    addFunds: async (fundData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.TREASURY_ADD, fundData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getTreasuryHistory: async (fundType = null, limit = 100) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.TREASURY_HISTORY, {
                params: {
                    ...(fundType && { fundType }),
                    limit
                }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Settings - Auction default prices
    getAuctionDefaultPrices: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.SETTINGS_AUCTION_DEFAULT_PRICES);
            return response;
        } catch (error) {
            throw error;
        }
    },

    saveAuctionDefaultPrices: async (prices) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.SETTINGS_AUCTION_DEFAULT_PRICES, prices);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Settings > Players
    getSettingsPlayersTeams: async () => {
        const response = await apiClient.get(API_ENDPOINTS.ADMIN.SETTINGS_PLAYERS_TEAMS);
        return response;
    },
    getSettingsPlayersByTeam: async (teamId, params = {}) => {
        const url = API_ENDPOINTS.ADMIN.SETTINGS_PLAYERS_TEAM_PLAYERS(teamId ?? 'null');
        const response = await apiClient.get(url, { params });
        return response;
    },
    syncPlayersLeagueTeam: async () => {
        const response = await apiClient.post(API_ENDPOINTS.ADMIN.PLAYERS_SYNC_LEAGUE_TEAM, null, { timeout: 20000 });
        return response;
    },
    getSyncLeagueTeamStatus: async () => {
        const response = await apiClient.get(API_ENDPOINTS.ADMIN.PLAYERS_SYNC_LEAGUE_TEAM_STATUS, { timeout: 15000 });
        return response;
    },
    getSettingsPlayersSyncChanges: async () => {
        const response = await apiClient.get(API_ENDPOINTS.ADMIN.SETTINGS_PLAYERS_SYNC_CHANGES);
        return response;
    },
    updateSettingsPlayerLeagueTeam: async (playerId, payload) => {
        const response = await apiClient.patch(API_ENDPOINTS.ADMIN.SETTINGS_PLAYERS_UPDATE(playerId), payload);
        return response;
    },

    // Marketplace Management
    getMarketplaceStats: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.MARKETPLACE_STATS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getMarketplaceActivity: async (days = 7) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.MARKETPLACE_ACTIVITY, {
                params: { days }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    cancelAuction: async (auctionId, cancelData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_CANCEL_AUCTION(auctionId), cancelData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    finalizeExpiredAuctions: async () => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_FINALIZE_EXPIRED_AUCTIONS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    blockCard: async (cardId, blockData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_BLOCK_CARD(cardId), blockData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    unblockCard: async (cardId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_UNBLOCK_CARD(cardId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    processRefund: async (refundData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_REFUNDS, refundData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getAbuseAlerts: async (filters = {}) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.MARKETPLACE_ALERTS, {
                params: filters
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    createAbuseAlert: async (alertData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_CREATE_ALERT, alertData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    resolveAbuseAlert: async (alertId, resolution) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_RESOLVE_ALERT(alertId), { resolution });
            return response;
        } catch (error) {
            throw error;
        }
    },

    dismissAbuseAlert: async (alertId, reason) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_DISMISS_ALERT(alertId), { reason });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Company Card Sales
    getCompanyCards: async (filters = {}) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.MARKETPLACE_COMPANY_CARDS, {
                params: filters
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getCompanyCardsFilterOptions: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.MARKETPLACE_COMPANY_CARDS_FILTER_OPTIONS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    deleteCompanyCards: async (templateId, quantity) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_COMPANY_CARDS_DELETE, {
                templateId: Number(templateId),
                quantity: Math.max(1, Number(quantity) || 1)
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getCompanyActiveListings: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.MARKETPLACE_COMPANY_ACTIVE_LISTINGS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getConcludedAuctions: async (params = {}) => {
        try {
            const requestParams = { limit: params.limit ?? 50, offset: params.offset ?? 0 };
            if (params.search != null && String(params.search).trim() !== '') {
                requestParams.search = String(params.search).trim();
            }
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.MARKETPLACE_CONCLUDED_AUCTIONS, {
                params: requestParams
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getConcludedSales: async (params = {}) => {
        try {
            const requestParams = { limit: params.limit ?? 50, offset: params.offset ?? 0 };
            if (params.search != null && String(params.search).trim() !== '') {
                requestParams.search = String(params.search).trim();
            }
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.MARKETPLACE_CONCLUDED_SALES, {
                params: requestParams
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    createCompanySale: async (saleData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_COMPANY_SALES, saleData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    createCompanyAuction: async (auctionData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_COMPANY_AUCTIONS, auctionData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    previewTeamAuctions: async (teamNames, startDate, rarities, startTime) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_COMPANY_AUCTIONS_PREVIEW_TEAM, { teamNames, startDate, rarities: rarities?.length ? rarities : undefined, startTime: startTime || undefined });
            return response;
        } catch (error) {
            throw error;
        }
    },

    createTeamBatchAuctions: async (teamNames, startDate, rarities, startTime) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_COMPANY_AUCTIONS_TEAM_BATCH, { teamNames, startDate, rarities: rarities?.length ? rarities : undefined, startTime: startTime || undefined });
            return response;
        } catch (error) {
            throw error;
        }
    },

    createCompanyLoan: async (loanData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.MARKETPLACE_COMPANY_LOANS, loanData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Update and Remove Company Listings
    updateSalePrice: async (saleId, newPrice) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.MARKETPLACE_UPDATE_SALE_PRICE(saleId), { newPrice });
            return response;
        } catch (error) {
            throw error;
        }
    },

    removeSale: async (saleId) => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.ADMIN.MARKETPLACE_REMOVE_SALE(saleId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateAuctionEndTime: async (auctionId, newEndTime) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.MARKETPLACE_UPDATE_AUCTION_END_TIME(auctionId), { newEndTime });
            return response;
        } catch (error) {
            throw error;
        }
    },

    removeAuction: async (auctionId) => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.ADMIN.MARKETPLACE_REMOVE_AUCTION(auctionId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateLoanDetails: async (loanId, updateData) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.MARKETPLACE_UPDATE_LOAN(loanId), updateData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    removeLoan: async (loanId) => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.ADMIN.MARKETPLACE_REMOVE_LOAN(loanId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Logs Management
    getLogs: async (filters = {}, limit = 100, offset = 0) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.LOGS, {
                params: { ...filters, limit, offset }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getLog: async (id) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GET_LOG(id));
            return response;
        } catch (error) {
            throw error;
        }
    },

    getLogsStatistics: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.LOGS_STATISTICS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Cards Management
    getPlayers: async (filters = {}) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.CARDS_GET_PLAYERS, {
                params: filters
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getRarities: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.CARDS_GET_RARITIES);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getLeagues: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.CARDS_GET_LEAGUES);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getTeamsByLeague: async (league) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.CARDS_GET_TEAMS, {
                params: { league }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getRemainingCopies: async (playerId, rarityId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.CARDS_GET_REMAINING, {
                params: { playerId, rarityId }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    createCard: async (cardData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.CARDS_CREATE, cardData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    createCardsBulk: async (cardData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.CARDS_CREATE_BULK, cardData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getCoachCards: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.COACH_CARDS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    createCoachCard: async (data) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.COACH_CARDS, data);
            return response;
        } catch (error) {
            throw error;
        }
    },

    assignCoachCardOwner: async (cardId, userId, templateId, rarity) => {
        try {
            const response = await apiClient.post(
                API_ENDPOINTS.ADMIN.COACH_CARDS_ASSIGN_OWNER(cardId),
                { userId, templateId, rarity }
            );
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Withdrawals Management (only send non-empty filters so backend returns all when "All" is selected)
    getWithdrawals: async (filters = {}) => {
        try {
            const params = {};
            ['status', 'userId', 'dateFrom', 'dateTo', 'minAmount', 'maxAmount', 'currency', 'limit', 'offset'].forEach((key) => {
                const v = filters[key];
                if (v !== undefined && v !== null && String(v).trim() !== '') params[key] = v;
            });
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.WITHDRAWALS, { params });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getWithdrawal: async (id) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GET_WITHDRAWAL(id));
            return response;
        } catch (error) {
            throw error;
        }
    },

    getWithdrawalStats: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.WITHDRAWALS_STATS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    approveWithdrawal: async (id, transactionHash = null) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.APPROVE_WITHDRAWAL(id), {
                transactionHash
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    rejectWithdrawal: async (id, reason) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.REJECT_WITHDRAWAL(id), {
                reason
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    triggerWithdrawalCryptoPay: async (id) => {
        try {
            const response = await apiClient.post(
                API_ENDPOINTS.ADMIN.PAY_WITHDRAWAL_CRYPTO(id),
                {},
                { timeout: 120000 }
            );
            return response;
        } catch (error) {
            throw error;
        }
    },

    getWithdrawalCryptoPayStatus: async (id) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.PAY_WITHDRAWAL_CRYPTO_STATUS(id), {
                timeout: 30000
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Affiliations
    getAffiliationStats: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.AFFILIATIONS_STATS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getUserReferralNetwork: async (userId) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.AFFILIATIONS_NETWORK(userId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    getReferrals: async (filters = {}) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.AFFILIATIONS_REFERRALS, {
                params: filters
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateReferralStatus: async (referralId, status) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.AFFILIATIONS_REFERRAL_STATUS(referralId), {
                status
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    changeReferrer: async (referralId, referrerId) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.AFFILIATIONS_REFERRAL_CHANGE_REFERRER(referralId), {
                referrerId
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getReferralEarnings: async (filters = {}) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.AFFILIATIONS_EARNINGS, {
                params: filters
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    blockUserAffiliation: async (userId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.AFFILIATIONS_USER_BLOCK(userId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    unblockUserAffiliation: async (userId) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.AFFILIATIONS_USER_UNBLOCK(userId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    getTopReferrers: async (limit = 10) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.AFFILIATIONS_TOP_REFERRERS, {
                params: { limit }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateReferralEarnings: async (earningsId, amount) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.AFFILIATIONS_EARNINGS_UPDATE(earningsId), {
                amount
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    deleteReferralEarnings: async (earningsId) => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.ADMIN.AFFILIATIONS_EARNINGS_DELETE(earningsId));
            return response;
        } catch (error) {
            throw error;
        }
    },

    createManualReferralEarnings: async (earningsData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.AFFILIATIONS_EARNINGS_CREATE, earningsData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Packs
    getPacks: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.PACKS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getPack: async (id) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GET_PACK(id));
            return response;
        } catch (error) {
            throw error;
        }
    },

    getPackStats: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.PACKS_STATS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    createPack: async (packData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.PACKS, packData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    updatePack: async (id, packData) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.GET_PACK(id), packData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    deletePack: async (id) => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.ADMIN.GET_PACK(id));
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Security Reports
    getSecurityReports: async (filters = {}, limit = 100, offset = 0) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.SECURITY_REPORTS, {
                params: { ...filters, limit, offset }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getSecurityReport: async (id) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.GET_SECURITY_REPORT(id));
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateSecurityReportStatus: async (id, status, adminNotes) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.UPDATE_SECURITY_REPORT_STATUS(id), {
                status,
                adminNotes
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Login History
    getLoginHistory: async (userId, limit = 100, offset = 0) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.USER_LOGIN_HISTORY(userId), {
                params: { limit, offset }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    getAllLoginHistory: async (userId, limit = 100, offset = 0) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.ALL_LOGIN_HISTORY, {
                params: { userId, limit, offset }
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Announcements
    getAnnouncements: async (filters = {}) => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.ANNOUNCEMENTS, {
                params: filters
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    createAnnouncement: async (announcementData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.CREATE_ANNOUNCEMENT, announcementData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateAnnouncement: async (id, announcementData) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.UPDATE_ANNOUNCEMENT(id), announcementData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    deleteAnnouncement: async (id) => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.ADMIN.DELETE_ANNOUNCEMENT(id));
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Maintenance Alerts
    getMaintenanceAlerts: async () => {
        try {
            const response = await apiClient.get(API_ENDPOINTS.ADMIN.MAINTENANCE_ALERTS);
            return response;
        } catch (error) {
            throw error;
        }
    },

    createMaintenanceAlert: async (alertData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.CREATE_MAINTENANCE_ALERT, alertData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    updateMaintenanceAlertStatus: async (id, status, actualStart, actualEnd) => {
        try {
            const response = await apiClient.put(API_ENDPOINTS.ADMIN.UPDATE_MAINTENANCE_ALERT_STATUS(id), {
                status,
                actualStart,
                actualEnd
            });
            return response;
        } catch (error) {
            throw error;
        }
    },

    deleteMaintenanceAlert: async (id) => {
        try {
            const response = await apiClient.delete(API_ENDPOINTS.ADMIN.DELETE_MAINTENANCE_ALERT(id));
            return response;
        } catch (error) {
            throw error;
        }
    },

    // Notifications
    createNotification: async (notificationData) => {
        try {
            const response = await apiClient.post(API_ENDPOINTS.ADMIN.CREATE_NOTIFICATION, notificationData);
            return response;
        } catch (error) {
            throw error;
        }
    },

    getPlatformEmailStatus: async () => {
        try {
            return await apiClient.get(API_ENDPOINTS.ADMIN.PLATFORM_EMAIL_STATUS);
        } catch (error) {
            throw error;
        }
    },

    sendPlatformEmail: async (payload) => {
        try {
            return await apiClient.post(API_ENDPOINTS.ADMIN.PLATFORM_EMAIL_SEND, payload);
        } catch (error) {
            throw error;
        }
    }
};

export default adminApi;
